import { useState } from "react";

import type { BancoOut } from "../../lib/cadastros/tipos";
import {
  TIPOS_CONTA,
  type ContaCorrenteOut,
  type TipoConta,
} from "../../lib/cadastros/tipos";
import { ErroHttp } from "../../lib/http/erros";

/*
 * Formulário controlado de conta corrente (FE-4 CP-B). Cobre os DOIS modos:
 *
 *  - CRIAÇÃO (`conta === undefined`): banco_id (dropdown via `bancos`), agencia,
 *    numero, tipo (∈ `TIPOS_CONTA`). `empresa_id` NUNCA vai no corpo (vem do path,
 *    fixado no hook do orquestrador). `saldo_disponivel` NÃO é cadastrável.
 *  - EDIÇÃO (`conta` presente): agencia + numero + tipo + toggle `ativa`. O banco
 *    NÃO é editável no PATCH (mostrado read-only, sem dropdown).
 *
 * O `saldo_disponivel` é STRING monetária READ-ONLY — formatado pelo caller, nunca
 * convertido para `number` nem editável aqui.
 *
 * Componente PURO: não conhece o hook de mutação. O orquestrador injeta `aoSalvar`,
 * `salvando` e o `erro` normalizado. SÓ tokens CPMH; labels em todos os inputs;
 * erro com `role="alert"`.
 */

/** Campos coletados. Na edição, `banco_id` é ignorado pelo caller. */
export interface DadosContaForm {
  banco_id: string;
  agencia: string;
  numero: string;
  tipo: TipoConta;
  ativa: boolean;
}

/** Rótulo pt-BR de cada tipo de conta (a chave técnica nunca aparece sozinha). */
const ROTULO_TIPO: Record<TipoConta, string> = {
  corrente: "Conta corrente",
  poupanca: "Poupança",
  garantida: "Conta garantida",
};

interface FormularioContaProps {
  /** Conta em edição, ou `undefined` para criação. */
  conta?: ContaCorrenteOut;
  /** Catálogo de bancos (para o dropdown na criação). */
  bancos: BancoOut[];
  aoSalvar: (dados: DadosContaForm) => void;
  aoCancelar: () => void;
  salvando: boolean;
  erro: unknown;
}

export function FormularioConta({
  conta,
  bancos,
  aoSalvar,
  aoCancelar,
  salvando,
  erro,
}: FormularioContaProps): JSX.Element {
  const ehEdicao = conta !== undefined;

  const [bancoId, setBancoId] = useState(conta?.banco_id ?? "");
  const [agencia, setAgencia] = useState(conta?.agencia ?? "");
  const [numero, setNumero] = useState(conta?.numero ?? "");
  const [tipo, setTipo] = useState<TipoConta>(conta?.tipo ?? TIPOS_CONTA[0]);
  const [ativa, setAtiva] = useState(conta?.ativa ?? true);
  const [erroBorda, setErroBorda] = useState<string | null>(null);

  function submeter(): void {
    // Validação de borda: campos obrigatórios.
    if (!ehEdicao && bancoId === "") {
      setErroBorda("Selecione o banco.");
      return;
    }
    if (agencia.trim() === "") {
      setErroBorda("Informe a agência.");
      return;
    }
    if (numero.trim() === "") {
      setErroBorda("Informe o número da conta.");
      return;
    }
    setErroBorda(null);
    aoSalvar({
      banco_id: bancoId,
      agencia: agencia.trim(),
      numero: numero.trim(),
      tipo,
      ativa,
    });
  }

  const nomeBancoAtual =
    bancos.find((banco) => banco.id === conta?.banco_id)?.nome ??
    conta?.banco_id ??
    "—";

  return (
    <div className="flex max-w-lg flex-col gap-4 rounded-md border border-platinum bg-snow p-6 font-corpo text-base text-off-black">
      <h2 className="font-titulo text-xl text-off-black">
        {ehEdicao ? "Editar conta" : "Nova conta"}
      </h2>

      {ehEdicao ? (
        <div className="flex flex-col gap-1">
          <span className="font-subtitulo">Banco</span>
          <span>{nomeBancoAtual}</span>
        </div>
      ) : (
        <label className="flex flex-col gap-1">
          <span className="font-subtitulo">Banco</span>
          <select
            value={bancoId}
            onChange={(evento) => setBancoId(evento.target.value)}
            className="rounded-md border border-platinum bg-snow px-2 py-1 text-off-black"
          >
            <option value="" disabled>
              Selecione…
            </option>
            {bancos.map((banco) => (
              <option key={banco.id} value={banco.id}>
                {banco.nome}
              </option>
            ))}
          </select>
        </label>
      )}

      <label className="flex flex-col gap-1">
        <span className="font-subtitulo">Agência</span>
        <input
          type="text"
          value={agencia}
          onChange={(evento) => setAgencia(evento.target.value)}
          className="rounded-md border border-platinum bg-snow px-2 py-1 text-off-black"
        />
      </label>

      <label className="flex flex-col gap-1">
        <span className="font-subtitulo">Número</span>
        <input
          type="text"
          value={numero}
          onChange={(evento) => setNumero(evento.target.value)}
          className="rounded-md border border-platinum bg-snow px-2 py-1 text-off-black"
        />
      </label>

      <label className="flex flex-col gap-1">
        <span className="font-subtitulo">Tipo</span>
        <select
          value={tipo}
          onChange={(evento) => setTipo(evento.target.value as TipoConta)}
          className="rounded-md border border-platinum bg-snow px-2 py-1 text-off-black"
        >
          {TIPOS_CONTA.map((valor) => (
            <option key={valor} value={valor}>
              {ROTULO_TIPO[valor]}
            </option>
          ))}
        </select>
      </label>

      {ehEdicao && (
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={ativa}
            onChange={(evento) => setAtiva(evento.target.checked)}
          />
          <span className="font-subtitulo">Ativa</span>
        </label>
      )}

      {erroBorda !== null && (
        <p role="alert" className="font-corpo text-base text-flame">
          {erroBorda}
        </p>
      )}

      {erro !== null && erro !== undefined && (
        <p role="alert" className="font-corpo text-base text-flame">
          {mensagemErroConta(erro)}
        </p>
      )}

      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={aoCancelar}
          disabled={salvando}
          className="rounded-md border border-platinum bg-snow px-4 py-1 text-off-black disabled:opacity-60"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={submeter}
          disabled={salvando}
          className="rounded-md border border-flame bg-flame px-4 py-1 text-snow disabled:opacity-60"
        >
          {salvando ? "Salvando…" : "Salvar"}
        </button>
      </div>
    </div>
  );
}

/**
 * Traduz o erro de uma mutação de conta numa mensagem estável. 409 (conflito —
 * ex.: agência/número duplicados) e 422 (validação) recebem mensagens claras;
 * demais reusam a mensagem genérica do `ErroHttp`. Erro não-`ErroHttp` → fallback.
 */
function mensagemErroConta(erro: unknown): string {
  if (erro instanceof ErroHttp) {
    if (erro.status === 409) {
      return "Já existe uma conta com estes dados.";
    }
    if (erro.status === 422) {
      return "Dados inválidos. Verifique os campos e tente novamente.";
    }
    return erro.message;
  }
  return "Não foi possível salvar a conta.";
}
